import React, { useMemo, useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { SectionPager } from '../components/SectionPager';
import { Reveal } from '../components/Reveal';
import { additionalSkills, endorsedSkills } from '../data/skills';
type Filter = 'all' | 'peer';
export function Skills() {
  const [filter, setFilter] = useState<Filter>('all');
  const visible = useMemo(() => filter === 'peer' ? endorsedSkills.filter(s => s.peers) : endorsedSkills, [filter]);
  const max = endorsedSkills[0].endorsements;
  const total = endorsedSkills.reduce((sum, s) => sum + s.endorsements, 0);
  return <>
      <PageHeader number="07" title="Skills & Endorsements" blurb="Peer-validated capability, ranked by endorsement across three decades." data-id="element-362" data-mp="s34b6vqikmh6" />

      <section className="bg-paper py-20 lg:py-24" data-id="element-363" data-mp="1kl9och9hjyui">
        <div className="mx-auto max-w-shell px-6 lg:px-10" data-id="element-364" data-mp="1kmugy1c8p7pq">
          <div className="flex flex-col gap-6 border-b border-navy/15 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-365" data-mp="1fthtvvn7l8z3">
            <Reveal data-id="element-366" data-mp="65dfwbn4orco">
              <p className="font-display text-3xl font-light text-navy" data-id="element-367" data-mp="b3osgu4t5fyl">
                {total}
                <span className="ml-3 text-[13px] uppercase tracking-overline text-steel" data-id="element-368" data-mp="b5hs3ph06zx3">
                  total endorsements across {endorsedSkills.length} skills
                </span>
              </p>
            </Reveal>

            <div className="flex gap-px" role="group" aria-label="Filter endorsements" data-id="element-369" data-mp="3jiypvoxr5x01">
              {([{
              id: 'all',
              label: 'All skills'
            }, {
              id: 'peer',
              label: 'Endorsed by experts'
            }] as {
              id: Filter;
              label: string;
            }[]).map(opt => <button key={opt.id} type="button" onClick={() => setFilter(opt.id)} aria-pressed={filter === opt.id} className={`border px-5 py-2.5 text-[11px] uppercase tracking-overline transition-colors duration-150 ease-out ${filter === opt.id ? 'border-navy bg-navy text-paper' : 'border-navy/20 text-steel hover:border-navy/50 hover:text-navy'}`} data-id="element-370" data-mp="3el6nu23e8ui4">
                  {opt.label}
                </button>)}
            </div>
          </div>

          <ul className="mt-2" data-id="element-371" data-mp="1u2cvbh6bdoe0">
            {visible.map(skill => <li key={skill.name} className="border-b border-navy/10" data-id="element-372" data-mp="1u4flc17gvhiq">
                <div className="grid gap-4 py-7 lg:grid-cols-12 lg:items-center lg:gap-8" data-id="element-373" data-mp="1p4ksvt81mjvk">
                  <div className="lg:col-span-4" data-id="element-374" data-mp="1kbi1nrqqnmij">
                    <h2 className="font-display text-xl font-light text-navy" data-id="element-375" data-mp="1fdgg905ubb3w">{skill.name}</h2>
                  </div>

                  <div className="lg:col-span-4" data-id="element-376" data-mp="2ctduo6bcjly3">
                    <div className="flex items-center gap-4" data-id="element-377" data-mp="2cu5e6iro5t80">
                      <span aria-hidden="true" className="h-[3px] bg-gold" style={{
                    width: `${Math.max(skill.endorsements / max * 100, 4)}%`
                  }} data-id="element-378" data-mp="281vqzahxv79f" />
                      <span className="whitespace-nowrap text-[12px] uppercase tracking-overline text-steel" data-id="element-379" data-mp="2kcn1pswdr8jz">
                        {skill.endorsements}{' '}
                        {skill.endorsements === 1 ? 'endorsement' : 'endorsements'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1 lg:col-span-4" data-id="element-380" data-mp="33b7t7m26ofy5">
                    {skill.peers && <p className="text-[13px] leading-relaxed text-navy/75" data-id="element-381" data-mp="381xvgqfbc2om">{skill.peers}</p>}
                    {skill.colleagues && <p className="text-[13px] leading-relaxed text-steel" data-id="element-382" data-mp="3d0j0r49f9cjx">{skill.colleagues}</p>}
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="border-t border-navy/10 bg-white py-16 lg:py-20" data-id="element-383" data-mp="nl7mkw4p7goo">
        <div className="mx-auto grid max-w-shell gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-384" data-mp="nj4orpxb0j9e">
          <div className="lg:col-span-4" data-id="element-385" data-mp="ik0hj4txwvlp">
            <Reveal data-id="element-386" data-mp="xbirakw7htwm">
              <p className="text-[11px] uppercase tracking-overline text-gold" data-id="element-387" data-mp="x9pjv3drr5nk">Also practiced</p>
              <h2 className="mt-5 font-display text-3xl font-light leading-tight text-navy" data-id="element-388" data-mp="scz82s8zyw7y">
                Additional capability
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8" data-id="element-389" data-mp="3vtg1qmcgj0mf">
            <Reveal delay={0.06} data-id="element-390" data-mp="3vuhf4xgy3z7b">
              <ul className="flex flex-wrap gap-x-3 gap-y-3" data-id="element-391" data-mp="dmrxszt58jic">
                {additionalSkills.map(s => <li key={s} className="border border-navy/15 px-4 py-2 text-[13px] text-navy/80" data-id="element-392" data-mp="9kww3u7aw441">
                    {s}
                  </li>)}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <SectionPager current="/skills" data-id="element-393" data-mp="1sjus2dp5eztd" />
    </>;
}